/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import React, { useMemo } from "react";
import Container from "../../../../components/Container/Container";
import Layout from "../../../Layouts/Layout";
import PartnerLogoSection from "../../../../components/Sections/Generic/PartnerLogoSection/PartnerLogoSection";
import LandingPageTeaser from "../../../../components/Teaser/LandingPageTeaser";
import AttorneySection from "../../../../components/Sections/Generic/AttorneySection/AttorneySection";
import FamilyLawConsultingCheckin from "../../../../components/Checkin/FamilyLawCheckin/Consulting/FamilyLawConsultingCheckin";
import { defaultProvenExpertTeaserCard } from "@/src/services/landingPageService";
import LinkedCardBox from "../../../../components/LinkedCardBox/LinkedCardBox";
import LpFamilyLawConsultingAdvoCardMeta from "./AdvoCard/LpFamilyLawConsultingAdvoCardMeta";
import LpFamilyLawConsultingAragMeta from "./Arag/LpFamilyLawConsultingAragMeta";
import LpFamilyLawConsultingDeuragMeta from "./Deurag/LpFamilyLawConsultingDeuragMeta";
import iconAttorneys from "../../../../assets/icons/generic/IconLawyers";
import iconDocument from "../../../../assets/icons/generic/IconDocument";
import { useCustomer } from "@/src/provider/Customer/CustomerProvider";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import HowItWorksSectionConsulting from "../../../../components/Sections/FamilyLaw/Consulting/HowItWorksSection/HowItWorksSectionConsulting";
import { PARTNER_IRIS } from "@/src/constants/partnerIris";
import LpFamilyLawConsultingLVMMeta from "./LVM/LpFamilyLawConsultingLVMMeta";
import LpFamilyLawConsultingAuxiliaMeta from "./Auxilia/LpFamilyLawConsultingAuxiliaMeta.tsx";

const getMetaDataByPartner = ({ partner }: { partner: string }) => {
  switch (partner) {
    case PARTNER_IRIS.ADVOCARD:
      return <LpFamilyLawConsultingAdvoCardMeta />;
    case PARTNER_IRIS.ARAG:
      return <LpFamilyLawConsultingAragMeta />;
    case PARTNER_IRIS.AUXILIA:
      return <LpFamilyLawConsultingAuxiliaMeta />;
    case PARTNER_IRIS.DEURAG:
      return <LpFamilyLawConsultingDeuragMeta />;
    case PARTNER_IRIS.LVM:
      return <LpFamilyLawConsultingLVMMeta />;
    default:
      return null;
  }
};

const LpFamilyLawConsultingPartner = ({ partner }: { partner: NonNullable<AbstractCase["acquisitionPartner"]> }) => {
  const { isLoggedIn } = useCustomer();
  const metaData = useMemo(() => getMetaDataByPartner({ partner }), [partner]);

  return (
    <Layout minimal={!isLoggedIn} clickableLogo={isLoggedIn} backgroundType={"landingpage"}>
      {metaData}
      <LandingPageTeaser
        teaserButtonText={"Jetzt Starten"}
        headline={partner === PARTNER_IRIS.AUXILIA ? "Professionelle Hilfe im Familien- und Erbrecht" : "Professionelle Hilfe im Familienrecht"}
        subheadline={"Kurz Anliegen schildern und Gespräch vereinbaren"}
        teaserButtonLink={isLoggedIn ? "/servicewelt" : "#top-checkin"}
      >
        <LinkedCardBox
          linkedCards={[
            defaultProvenExpertTeaserCard,
            {
              icon: iconAttorneys,
              heading: "Professionelle Beratung",
            },
            {
              icon: iconDocument,
              heading: "Unkomplizierte Abwicklung",
            },
          ]}
        />
      </LandingPageTeaser>
      <PartnerLogoSection partnerIri={partner} containerStyle={"settlementContainer"} />
      {!isLoggedIn && (
        <Container>
          <FamilyLawConsultingCheckin idPrefix={"top"} partner={partner} />
        </Container>
      )}
      <HowItWorksSectionConsulting />
      <AttorneySection
        subheading={"Sie möchten sich professionell und schnell unterstützen lassen? Wir helfen Ihnen - jederzeit und ortsunabhängig!"}
        heading={"Deutschlandweit tätige Partneranwälte"}
      />
      {!isLoggedIn && (
        <Container>
          <FamilyLawConsultingCheckin idPrefix={"top"} partner={partner} />
        </Container>
      )}
    </Layout>
  );
};

export default LpFamilyLawConsultingPartner;
